// src/Pages/DashboardContent.jsx
import React, { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { baseurl } from "../config/url";
import { CATEGORY_CONFIG } from "../config/categoryConfig";
import { CategoryIcon } from "../Components/CategoryIcon";

const DashboardContent = () => {
  const categories = Object.values(CATEGORY_CONFIG);
  const [selectedCategoryId, setSelectedCategoryId] = useState(
    categories[0]?.id || null
  );
  const [products, setProducts] = useState([]);
  const [loadingProducts, setLoadingProducts] = useState(false);

  // Fetch products when category changes
  useEffect(() => {
    if (!selectedCategoryId) return;

    const fetchProducts = async () => {
      try {
        setLoadingProducts(true);
        const res = await axios.get(
          `${baseurl}/api/products?category=${selectedCategoryId}&page=1&limit=12`
        );
        setProducts(res.data?.products || []);
      } catch (err) {
        console.error("Error fetching category products:", err);
        setProducts([]);
      } finally {
        setLoadingProducts(false);
      }
    };

    fetchProducts();
  }, [selectedCategoryId]);

  const selectedMeta =
    categories.find((c) => c.id === selectedCategoryId) || categories[0];

  return (
    <div className="space-y-4">
      {/* MAIN LAYOUT: SIDEBAR + RIGHT PANEL */}
      <section className="bg-white/95 border border-slate-200 rounded-2xl px-3 py-3 md:px-4 md:py-4 shadow-sm">
        <div className="flex flex-col md:flex-row gap-3 md:gap-4">

          {/* LEFT SIDEBAR – CATEGORIES */}
          <aside className="md:w-64 w-full md:border-r border-slate-200 md:pr-3">
            <div className="mb-2">
              <h2 className="text-sm font-semibold text-slate-900">
                Categories
              </h2>
            </div>

            <div className="flex md:flex-col gap-2 overflow-x-auto md:overflow-visible pb-1 md:pb-0">
              {categories.map((cat) => {
                const isActive = cat.id === selectedCategoryId;
                return (
                  <button
                    key={cat.id}
                    type="button"
                    onClick={() => setSelectedCategoryId(cat.id)}
                    className={`flex items-center gap-2 rounded-xl border px-3 py-2 text-xs min-w-[150px] md:w-full transition ${
                      isActive
                        ? "border-[#00204E] bg-[#EFF6FF] text-[#00204E] shadow-sm"
                        : "border-slate-200 bg-slate-50/80 text-slate-800 hover:border-slate-400 hover:bg-white"
                    }`}
                  >
                    <CategoryIcon
                      name={cat.icon}
                      className={`w-5 h-5 ${
                        isActive ? "text-[#00204E]" : "text-slate-700"
                      }`}
                    />
                    <span className="font-medium truncate">{cat.label}</span>
                  </button>
                );
              })}
            </div>
          </aside>

          {/* RIGHT SIDE – CATEGORY PRODUCTS */}
          <div className="flex-1">
            {/* Category Header */}
            <div className="flex items-center justify-between gap-2 mb-3">
              <div className="flex items-center gap-2">
                <CategoryIcon
                  name={selectedMeta?.icon}
                  className="w-7 h-7 text-slate-800"
                />
                <h2 className="text-sm md:text-base font-semibold text-slate-900">
                  {selectedMeta?.label}
                </h2>
              </div>

              <Link
                to={`/products/${selectedMeta?.id}`}
                className="text-[11px] font-semibold text-[#00204E] hover:underline"
              >
                View all
              </Link>
            </div>

            {/* Products Grid */}
            {loadingProducts ? (
              <p className="text-[11px] text-slate-500">Loading products...</p>
            ) : products.length === 0 ? (
              <p className="text-[11px] text-slate-500">No products found.</p>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
                {products.map((p) => (
                  <div
                    key={p._id}
                    className="rounded-xl border border-slate-200 bg-white overflow-hidden flex flex-col hover:shadow-sm transition"
                  >
                    <div className="aspect-[4/3] bg-slate-100 flex items-center justify-center text-[11px] text-slate-400">
                      {p.images?.[0] ? (
                        <img
                          src={`${baseurl}/${p.images[0]}`}
                          alt={p.name}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        "No image"
                      )}
                    </div>

                    <div className="p-2 flex-1 flex flex-col gap-1">
                      <p className="text-xs font-medium text-slate-900 line-clamp-2">
                        {p.name}
                      </p>
                      <p className="text-[11px] text-slate-500 line-clamp-2">
                        {p.description}
                      </p>
                      <p className="text-[11px] font-semibold text-[#00204E] mt-auto">
                        ₹ {p.price}
                        {p.priceUnit && (
                          <span className="text-slate-500">
                            {" "}
                            / {p.priceUnit}
                          </span>
                        )}
                      </p>

                      <div className="flex justify-between items-center mt-1">
                        <Link
                          to={`/product/details/${p._id}`}
                          className="text-[11px] text-[#00204E] hover:underline"
                        >
                          View details
                        </Link>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

        </div>
      </section>
    </div>
  );
};

export default DashboardContent;
